import colors from 'shared/colors';
import { UNASSIGNED_VALUE } from '@/composables/grid-page/chart/utils';
import { gridPageChartColorScheme, optionColor } from 'shared/constants.json';
import { rgbaToHex } from 'shared/lib/color';

export const getColor = (color) => {
  switch (color) {
    case optionColor.default:
    case optionColor.grey:
      return rgbaToHex(colors.grey.lighten3);
    case optionColor.brown:
      return colors.brown.base;
    case optionColor.orange:
      return colors.orange.lighten1;
    case optionColor.yellow:
      return colors.yellow.lighten1;
    case optionColor.green:
      return colors.green.lighten1;
    case optionColor.blue:
      return colors.blue.lighten1;
    case optionColor.purple:
      return colors.purple.lighten1;
    case optionColor.pink:
      return colors.pink.lighten1;
    case optionColor.red:
      return colors.red.lighten1;
    default:
      return rgbaToHex(colors.grey.lighten3);
  }
};

const gradientScales = {
  [gridPageChartColorScheme.grey]: [
    rgbaToHex(colors.grey.darken4),
    rgbaToHex(colors.grey.darken2),
    rgbaToHex(colors.grey.base),
    rgbaToHex(colors.grey.lighten2),
    rgbaToHex(colors.grey.lighten4),
  ],
};

const colorKeys = ['lighten1', 'lighten2', 'lighten3', 'lighten4', 'lighten5'];

[
  gridPageChartColorScheme.brown,
  gridPageChartColorScheme.yellow,
  gridPageChartColorScheme.orange,
  gridPageChartColorScheme.red,
  gridPageChartColorScheme.pink,
  gridPageChartColorScheme.purple,
  gridPageChartColorScheme.blue,
  gridPageChartColorScheme.green,
].forEach((name) => {
  gradientScales[name] = colorKeys.map((key) => colors[name][key]);
});

export const mcColorSet = [
  colors.blue.lighten1,
  colors.yellow.lighten1,
  colors.green.lighten1,
  colors.purple.lighten1,
  colors.orange.lighten1,
  colors.pink.lighten1,
  colors.slate.lighten1,
  colors.red.lighten1,
];

export const getGradientPalette = (colorName, count) => {
  const scale = gradientScales[colorName];
  if (!scale) {
    return Array(count).fill(rgbaToHex(colors.grey.lighten3));
  }
  if (count === 1) {
    return [scale[Math.floor(scale.length / 2)]];
  }
  if (count > scale.length) {
    return Array.from({ length: count }, (_, i) => scale[i % scale.length]);
  }
  return Array.from({ length: count }, (_, i) => scale[Math.round((i * (scale.length - 1)) / (count - 1))]);
};

const defaultColor = rgbaToHex(colors.grey.lighten3);

const hasDistinctColors = (categories) => categories.some((cat) => cat.hasEntityColor || (cat.color && cat.color !== defaultColor));

export const getSeriesColor = (scheme) => {
  if (!scheme || scheme === gridPageChartColorScheme.automatic || scheme === gridPageChartColorScheme.colorful) {
    return mcColorSet[0];
  }
  const scale = gradientScales[scheme];
  return scale ? scale[Math.floor(scale.length / 2)] : mcColorSet[0];
};

export const applyColorScheme = (scheme, categories, { colorUnassigned = false } = {}) => {
  if (!scheme || scheme === gridPageChartColorScheme.automatic) {
    if (hasDistinctColors(categories)) {
      return categories;
    }
    return applyColorScheme(gridPageChartColorScheme.colorful, categories, { colorUnassigned });
  }
  if (scheme === gridPageChartColorScheme.colorful) {
    let colorIdx = 0;
    return categories.map((cat) => {
      if (!colorUnassigned && cat.originalValue === UNASSIGNED_VALUE) {
        return cat;
      }
      const color = mcColorSet[colorIdx % mcColorSet.length];
      colorIdx += 1;
      return { ...cat, color };
    });
  }
  const palette = getGradientPalette(scheme, categories.length);
  return categories.map((cat, i) => ({
    ...cat,
    color: palette[i],
  }));
};
